import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Pagination,
  Spinner,
  Table,
} from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN } from "../../config/apiConfig";
import { formatThousands, formatToReal } from "../../utils/utils";
import AutoCompleteInput from "../inputs/Autocomplete";
import EmptyTable from "./EmptyTable";
import {
  MdDeleteOutline,
  MdLocalPrintshop,
  MdOutlineAttachMoney,
} from "react-icons/md";
import { BsFillBarChartFill } from "react-icons/bs";
import DrawerLoading from "../DrawerLoading";
import { useReactToPrint } from "react-to-print";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import TableLoading from "../TableLoading";
import { LuFilter, LuFilterX } from "react-icons/lu";
import { useApplication } from "../../contexts/ApplicationContext";
import { TbEye, TbEyePlus } from "react-icons/tb";
import { Tooltip } from "react-tooltip";

// ===== Helpers de moeda (máscara pt-BR enquanto digita) =====
const onlyDigits = (s = "") => s.replace(/[^\d]/g, "");
const formatTypingBRL = (s) => {
  // trata string de dígitos como centavos -> "123456" => "1.234,56"
  const digits = onlyDigits(String(s));
  if (!digits) return "0,00";
  const cents = digits.slice(-2).padStart(2, "0");
  const ints = digits.slice(0, -2) || "0";
  const intFormatted = Number(ints).toLocaleString("pt-BR");
  return `${intFormatted},${cents}`;
};
const parseBRLInputToNumber = (s) => {
  if (s == null) return 0;
  const digits = onlyDigits(String(s));
  if (!digits) return 0;
  return Number(digits) / 100;
};
const numberToBRLTyping = (n) => {
  const cents = Math.round(Number(n || 0) * 100);
  return formatTypingBRL(String(cents));
};

const AdminCloseTable = ({ isActive = true }) => {
  const queryClient = useQueryClient();
  const { userPermission, user } = useApplication();
  const isRepresentativeUser =
    userPermission === "REPRESENTANTE" ||
    user?.Company?.companyTypeId === "REPRESENTANTE";
  const canPickRepresentative =
    userPermission === "POWER_USER" || isRepresentativeUser;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const onPageChange = (page) => setCurrentPage(page);
  const [selectedCustomer, setSelectedCustomer] = useState({
    label: "Todos os clientes",
    value: 0,
  });
  const reportRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef: reportRef });
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypePayments, setSelectedTypePayments] = useState("");
  const [selectedStatusCustomer, setSelectedStatusCustomer] = useState("");
  const [openModalPaymentConfirm, setOpenModalPaymentConfirm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [errorPaymentConfirm, setErrorPaymentConfirm] = useState(false);
  const [errorPaymentDelete, setErrorPaymentDelete] = useState(false);
  const [openModalPaymentDelete, setOpenModalPaymentDelete] = useState(false);
  const defaultPeriod = useMemo(() => {
    const today = moment();
    const isLastDayOfMonth = today.isSame(today.clone().endOf("month"), "day");
    const base = isLastDayOfMonth ? today : today.clone().subtract(1, "month");
    return {
      startDate: base.clone().startOf("month").format("YYYY-MM-DD"),
      endDate: base.clone().endOf("month").format("YYYY-MM-DD"),
    };
  }, []);
  const [startDate, setStartDate] = useState(defaultPeriod.startDate);
  const [endDate, setEndDate] = useState(defaultPeriod.endDate);
  const [openModalRunClosing, setOpenModalRunClosing] = useState(false);
  const [selectedCustomersForClosing, setSelectedCustomersForClosing] =
    useState([]);
  const [dueDate, setDueDate] = useState(moment().format("YYYY-MM-DD"));
  const [isSubmittingClosing, setIsSubmittingClosing] = useState(false);
  const [closingResult, setClosingResult] = useState(null); // resultado da resposta da API
  const [showResultModal, setShowResultModal] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [renderFilters, setRenderFilters] = useState(false);
  const [description, setDescription] = useState("");
  const [selectedRepresentative, setSelectedRepresentative] = useState({
    label: "Todos os representantes",
    value: 0,
  });
  const [openModalSintetico, setOpenModalSintetico] = useState(false);
  const [openModalDetalhado, setOpenModalDetalhado] = useState(false);
  const [modalCompanyId, setModalCompanyId] = useState(null);
  const [modalCompanyName, setModalCompanyName] = useState("");
  const [modalPage, setModalPage] = useState(1); // se for paginar no detalhado
  const [modalPageSintetico, setModalPageSintetico] = useState(1);
  const [modalPageDetalhado, setModalPageDetalhado] = useState(1);
  const [invoiceSelectedForModal, setInvoiceSelectedForModal] = useState(null);

  // ===== NOVO: Preços editados por companyId (string "pt-BR" enquanto digita) =====
  const [editedPrices, setEditedPrices] = useState({}); // { [companyId]: "1.234,56" }

  useEffect(() => {
    if (showFilters) {
      setRenderFilters(true);
    } else {
      const timeout = setTimeout(() => setRenderFilters(false), 500);
      return () => clearTimeout(timeout);
    }
  }, [showFilters]);

  const {
    isLoading: isLoadingListRepresentatives,
    error: errorListRepresentatives,
    data: listRepresentatives,
  } = useQuery(
    ["listRepresentatives", userPermission, user?.companyId],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_REPRESENTATIVES}?itemsPerPage=-1`
      );

      const repsRaw = Array.isArray(data?.data?.data) ? data.data.data : [];
      const getParentId = (rep) =>
        rep?.parentId ?? rep?.ParentCompany?.id ?? rep?.representativeId ?? null;

      let repsForOptions = repsRaw;

      if (isRepresentativeUser) {
        const rootId = user?.companyId ?? user?.Company?.id ?? null;
        if (!rootId) {
          repsForOptions = [];
        } else {
          const childrenMap = new Map();
          repsRaw.forEach((rep) => {
            const parentId = getParentId(rep);
            if (!childrenMap.has(parentId)) {
              childrenMap.set(parentId, []);
            }
            childrenMap.get(parentId).push(rep);
          });

          const stack = [...(childrenMap.get(rootId) || [])];
          const descendants = [];

          while (stack.length) {
            const current = stack.shift();
            if (!current) continue;
            descendants.push(current);
            const kids = childrenMap.get(current.id) || [];
            stack.push(...kids);
          }

          repsForOptions = descendants;
        }
      }

      const representatives = [
        { label: "Todos os representantes", value: "" },
        ...(isRepresentativeUser ? [] : [{ label: "Relinq Score", value: 1 }]),
        ...repsForOptions.map((representative) => ({
          value: representative.id,
          label: representative?.name || representative?.socialReason,
        })),
      ];

      return representatives;
    },
    { enabled: canPickRepresentative }
  );

  const {
    isLoading: isLoadingCustomers,
    error: errorListCustomers,
    data: listCustomers,
  } = useQuery(["listCustomers", selectedRepresentative], async () => {
    try {
      const { data } = await axios.get(
        `${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1&representativeId=${
          selectedRepresentative?.value || ""
        }`
      );

      if (!data?.data?.data) {
        throw new Error("Erro ao buscar clientes");
      }

      let customers = data.data.data.map((customer) => ({
        label:
          customer.typeCustomer === "CPF"
            ? customer.name
            : customer.socialReason,
        value: customer.id,
        vcm: customer.vcm,
        typePayment: customer.typePayment,
        credit: customer.credit,
      }));

      customers = [{ label: "Todos os clientes", value: 0 }, ...customers];

      return customers;
    } catch (error) {
      throw new Error(`Erro ao buscar clientes: ${error.message}`);
    }
  });

  const {
    isLoading: isLoadingInvoices,
    error: errorInvoices,
    data: invoices,
  } = useQuery(
    [
      "invoices",
      startDate,
      endDate,
      selectedStatuses,
      selectedTypePayments,
      selectedCustomer,
      selectedRepresentative,
      selectedTypePayments,
      currentPage,
      selectedStatusCustomer,
    ],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_FINANCIAL_REPORT}?startDate=${startDate}&endDate=${endDate}&typePayment=${selectedTypePayments}&companyId=${selectedCustomer?.value}&representativeId=${selectedRepresentative?.value}&selectedTypePayment=${selectedTypePayments}&page=${currentPage}&status=${selectedStatusCustomer}`
      );

      setTotalPages(data.data.totalPages);
      setTotalCount(data.data.totalRecords);
      setTotalPriceSum(data.data.totalPrice);

      return data.data.results;
    }
  );

  // SINTÉTICO
  const {
    isLoading: isLoadingReportSimplifiedQueries,
    isFetching: isFetchingReportSimplifiedQueries,
    error: errorListReportSimplifiedQueries,
    data: listReportSimplifiedQueries,
  } = useQuery(
    [
      "listReportSimplifiedQueriesModal",
      modalCompanyId,
      startDate,
      endDate,
      modalPage,
      selectedStatuses,
    ],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_COMPANY_QUERIES_HISTORY_SIMPLIFIED}/${modalCompanyId}` +
          `?startDate=${startDate}&endDate=${endDate}` +
          `&page=${modalPage}` +
          `&queries=0&status=1` +
          `&isModular=0`
      );
      return data.data; // { data: [...], totalSumPrice, totalSumCount, totalPages? }
    },
    { enabled: openModalSintetico && !!modalCompanyId, keepPreviousData: true }
  );

  // DETALHADO
  const {
    isLoading: isLoadingReportQueries,
    isFetching: isFetchingReportQueries,
    error: errorListReportQueries,
    data: listReportQueries,
  } = useQuery(
    ["listReportQueriesModal", modalCompanyId, startDate, endDate, modalPage],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_COMPANY_QUERIES_HISTORY}/${modalCompanyId}` +
          `?startDate=${startDate}&endDate=${endDate}` +
          `&page=${modalPage}` +
          `&queries=0&status=1` +
          `&isModular=0`
      );
      return data.data; // { data: [...], totalPriceSum, totalCount, totalPages? }
    },
    { enabled: openModalDetalhado && !!modalCompanyId, keepPreviousData: true }
  );

  const handleUpdatePayment = async (type) => {
    setErrorPaymentConfirm(false);
    try {
      await axios.get(
        `${ADMIN.UPDATE_PAYMENT}/${selectedInvoice?.id}?companyId=${
          type === "CONFIRM_UPDATE" ? selectedInvoice?.companyId : ""
        }`
      );

      setOpenModalPaymentConfirm(false);
      queryClient.invalidateQueries(["invoices"]);
    } catch (error) {
      setErrorPaymentConfirm(true);
    }
  };

  const handleDeletePayment = async () => {
    setErrorPaymentDelete(false);
    try {
      await axios.delete(`${ADMIN.DELETE_PAYMENT}/${selectedInvoice?.id}`);

      setOpenModalPaymentDelete(false);
      queryClient.invalidateQueries(["invoices"]);
    } catch (error) {
      setErrorPaymentDelete(true);
    }
  };

  const formatPeriod = () =>
    `${startDate ? moment(startDate).format("DD/MM/YYYY") : "--/--/----"} a ${
      endDate ? moment(endDate).format("DD/MM/YYYY") : "--/--/----"
    }`;

  const currency = (v) => formatToReal(Number(v || 0));
  const resolveAdditionalFlag = (row) => {
    if (!row) return false;
    if (row.isAdditional != null) return Boolean(row.isAdditional);
    if (row.additional != null) return Boolean(row.additional);
    if (row.additionalName != null) return String(row.additionalName).trim() !== "";
    return false;
  };
  const resolveAdditionalLabel = (row) => {
    if (!row) return "Não";
    const name = row?.additionalName;
    if (name != null && String(name).trim() !== "") return String(name).trim();
    if (resolveAdditionalFlag(row)) {
      return row?.queryName || row?.name || "Sim";
    }
    return "Não";
  };

  useEffect(() => {
    if (!openModalSintetico && !openModalDetalhado) {
      setModalCompanyId(null);
      setModalCompanyName("");
      setModalPage(1);
    }
  }, [openModalSintetico, openModalDetalhado]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    startDate,
    endDate,
    selectedStatuses,
    selectedTypePayments,
    selectedCustomer,
    selectedRepresentative,
    selectedTypePayments,
    selectedStatusCustomer,
  ]);

  const appliedFilterSummary = useMemo(() => {
    const items = [];
    const repValue = selectedRepresentative?.value;
    if (
      canPickRepresentative &&
      repValue !== undefined &&
      repValue !== null &&
      repValue !== 0 &&
      repValue !== ""
    ) {
      items.push(`Representante: ${selectedRepresentative?.label || "-"}`);
    }
    if (selectedCustomer?.value && selectedCustomer?.value !== 0) {
      items.push(`Cliente: ${selectedCustomer?.label || "-"}`);
    }
    if (startDate || endDate) {
      const startLabel = startDate
        ? moment(startDate).format("DD/MM/YYYY")
        : "—";
      const endLabel = endDate ? moment(endDate).format("DD/MM/YYYY") : "—";
      items.push(`Período: ${startLabel} a ${endLabel}`);
    }
    if (selectedTypePayments) {
      const label =
        selectedTypePayments === "PRE"
          ? "Pré-Pago"
          : selectedTypePayments === "POS"
          ? "Pós-Pago"
          : selectedTypePayments;
      items.push(`Tipo: ${label}`);
    }
    if (selectedStatusCustomer !== "") {
      const label =
        Number(selectedStatusCustomer) === 1
          ? "Ativos"
          : Number(selectedStatusCustomer) === 0
          ? "Inadimplentes"
          : "Bloqueados";
      items.push(`Status: ${label}`);
    }
    return items;
  }, [
    userPermission,
    selectedRepresentative?.value,
    selectedRepresentative?.label,
    selectedCustomer?.value,
    selectedCustomer?.label,
    startDate,
    endDate,
    selectedTypePayments,
    selectedStatusCustomer,
  ]);
  const hasActiveFilters = appliedFilterSummary.length > 0;

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <Button
          size="xs"
          onClick={() => setShowFilters((prev) => !prev)}
          className="bg-white text-slate-700 border border-slate-200 shadow-sm hover:bg-slate-50 transition-all duration-300 hover:text-white"
        >
          <div className="flex items-center gap-2 transition-all duration-300">
            <span>{showFilters ? "Esconder filtros" : "Mostrar filtros"}</span>
            <span className="transition-transform duration-300">
              {!showFilters ? <LuFilter /> : <LuFilterX />}
            </span>
          </div>
        </Button>
      </div>
      <div className="flex flex-col gap-4">
        {showFilters && (
          <div className="transition-all duration-500 ease-in-out overflow-hidden bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            {renderFilters && (
              <div className="transition-opacity duration-500 min-w-[200px]">
                {isLoadingCustomers && <DrawerLoading />}
                <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-gray-50">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Filtros Avançados
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 px-3 py-3">
                  {canPickRepresentative && (
                    <div className="flex flex-col md:col-span-3">
                      <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                        Representante
                      </label>
                      <div className="h-9">
                        <AutoCompleteInput
                          options={listRepresentatives}
                          label=""
                          onChange={(representative) =>
                            setSelectedRepresentative(representative)
                          }
                          value={selectedRepresentative?.value}
                          isLoading={isLoadingListRepresentatives}
                          className="h-full"
                        />
                      </div>
                      {errorListCustomers && (
                        <Alert color="failure" className="mt-2">
                          Ocorreu um erro ao buscar clientes
                        </Alert>
                      )}
                    </div>
                  )}
                  <div className="flex flex-col md:col-span-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Cliente
                    </label>
                    <div className="h-9">
                      <AutoCompleteInput
                        options={listCustomers}
                        label=""
                        onChange={(customer) => setSelectedCustomer(customer)}
                        value={selectedCustomer?.value}
                        isLoading={isLoadingCustomers}
                        className="h-full"
                      />
                    </div>
                    {errorListCustomers && (
                      <Alert color="failure">
                        Ocorreu um erro ao buscar clientes
                      </Alert>
                    )}
                  </div>
                  <div className="flex flex-col md:col-span-3 mt-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Período
                    </label>
                    <div className="flex items-center gap-2 h-9">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (moment(e.target.value).isAfter(moment(endDate))) {
                            setEndDate(e.target.value);
                          }
                        }}
                        className="w-full h-full bg-white border border-slate-300 text-xs rounded-md px-2.5 py-2 focus:ring-slate-500 focus:border-slate-500"
                      />
                      <div className="text-[11px] text-slate-500">até</div>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                        }}
                        min={startDate}
                        className="w-full h-full bg-white border border-slate-300 text-xs rounded-md px-2.5 py-2 focus:ring-slate-500 focus:border-slate-500"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col md:col-span-1 mt-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Tipo de pagamento
                    </label>
                    <select
                      value={selectedTypePayments || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedTypePayments(val ? val : "");
                      }}
                      className="w-full h-9 rounded-md border border-slate-300 bg-white text-xs text-slate-700 px-2.5"
                    >
                      <option value="">Todos</option>
                      <option value="POS">Pós-Pago</option>
                      <option value="PRE">Pré-Pago</option>
                    </select>
                  </div>
                  <div className="flex flex-col md:col-span-2 mt-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Status do cliente
                    </label>
                    <select
                      value={selectedStatusCustomer || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedStatusCustomer(val ? val : "");
                      }}
                      className="w-full h-9 rounded-md border border-slate-300 bg-white text-xs text-slate-700 px-2.5"
                    >
                      <option value="">Todos os clientes</option>
                      <option value={1}>Ativos</option>
                      <option value={0}>Inadimplentes</option>
                      <option value={-1}>Bloqueados</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <Alert className="info" withBorderAccent>
          O fechamento dos representantes é realizado individualmente. Acesse o
          menu “Representantes” para visualizar e concluir os fechamentos
          individuais.
        </Alert>

        <div className="flex-1 print-padding print-content" ref={reportRef}>
          <div className="mt-3">
            <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 px-4 py-2 shadow-sm md:flex-row md:items-center md:justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 shadow-inner">
                  <BsFillBarChartFill className="text-xl" />
                </div>
                <p className="text-base font-semibold text-gray-900">
                  Resultado da busca
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-end text-xs">
                {hasActiveFilters ? (
                  <>
                    <span className="uppercase tracking-wide text-gray-500">
                      Filtros ativos
                    </span>
                    {appliedFilterSummary.slice(0, 4).map((item, index) => (
                      <span
                        key={`${item}-${index}`}
                        className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200 shadow-sm"
                      >
                        {item}
                      </span>
                    ))}
                    {appliedFilterSummary.length > 4 && (
                      <span className="px-2 py-1 rounded-full bg-gray-50 text-gray-500 text-xs font-medium border border-dashed border-gray-300">
                        +{appliedFilterSummary.length - 4} oculto(s)
                      </span>
                    )}
                  </>
                ) : (
                  <span className="uppercase tracking-wide text-gray-500">
                    Nenhum filtro aplicado
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="flex gap-4 pb-3 min-w-[500px]">
              <div className="min-w-[200px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#1E429F] to-[#3B82F6] relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Valor Total</p>
                    <h2 className="text-2xl font-bold mt-1">
                      {!isLoadingInvoices ? (
                        formatToReal(totalPriceSum)
                      ) : (
                        <div className="animate-pulse h-6 bg-white/30 rounded w-28" />
                      )}
                    </h2>
                  </div>
                  <MdOutlineAttachMoney className="text-white text-4xl opacity-20" />
                </div>
              </div>
              <div className="min-w-[200px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#0F766E] to-[#14B8A6] relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Total de clientes</p>
                    <h2 className="text-2xl font-bold mt-1">
                      {!isLoadingInvoices ? (
                        totalCount
                      ) : (
                        <div className="animate-pulse h-6 bg-white/30 rounded w-24" />
                      )}
                    </h2>
                  </div>
                  <BsFillBarChartFill className="text-white text-4xl opacity-20" />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4 mt-2 flex flex-col md:flex-row  justify-between items-end md:items-center gap-2">
            <Button
              className="bg-gray-500"
              onClick={async () => {
                try {
                  const params = new URLSearchParams();
                  if (startDate) params.set("startDate", startDate);
                  if (endDate) params.set("endDate", endDate);
                  if (selectedTypePayments)
                    params.set("selectedTypePayment", selectedTypePayments);
                  if (selectedCustomer?.value != null)
                    params.set("companyId", selectedCustomer.value);
                  if (selectedRepresentative?.value != null)
                    params.set(
                      "representativeId",
                      selectedRepresentative.value
                    );
                  if (selectedStatusCustomer !== "")
                    params.set("status", selectedStatusCustomer);

                  const url = `${
                    ADMIN.EXPORT_FINANCIAL_REPORT
                  }?${params.toString()}`;

                  const res = await axios.get(url, { responseType: "blob" });

                  const blob = new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });

                  const a = document.createElement("a");
                  const labelInicio = startDate
                    ? moment(startDate).format("YYYY-MM-DD")
                    : "inicio";
                  const labelFim = endDate
                    ? moment(endDate).format("YYYY-MM-DD")
                    : "fim";
                  a.href = URL.createObjectURL(blob);
                  a.download = `relatorio_financeiro_${labelInicio}_a_${labelFim}.xlsx`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(a.href);
                } catch (e) {
                  console.error("Falha ao exportar XLSX", e);
                  alert("Não foi possível exportar o XLSX. Tente novamente.");
                }
              }}
            >
              <div className="flex gap-2 items-center">
                <div>Exportar Relatório (XLSX)</div>
              </div>
            </Button>

            <Button
              color="success"
              onClick={() => setOpenModalRunClosing(true)}
            >
              Rodar fechamento financeiro
            </Button>
          </div>

          <small
            className="gap-2 items-center mt-5 mb-1 flex md:hidden justify-center"
            style={{ fontSize: 10 }}
          >
            <span>
              Deslize para o lado para visualizar todas as informações
            </span>{" "}
            <FaArrowsLeftRight />
          </small>
          {invoices?.length > 0 || isLoadingInvoices ? (
            <div className="overflow-x-auto shadow-md rounded-lg mt-0 md:mt-5">
              <Table striped style={{ fontSize: "13px" }}>
                <Table.Head>
                  <Table.HeadCell className="bg-gray-300 p-2">
                    Cliente
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-gray-300 p-2">
                    Vl. Utilizado
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-gray-300 p-2">
                    Consumo mínimo
                  </Table.HeadCell>

                  <Table.HeadCell className="text-center bg-gray-300 p-2">
                    Custo
                  </Table.HeadCell>

                  {user?.Company?.companyTypeId === "REPRESENTANTE" && (
                    <Table.HeadCell className="text-center bg-gray-300 p-2">
                      Lucro
                    </Table.HeadCell>
                  )}
                  <Table.HeadCell className="text-center bg-gray-300 p-2">
                    Vl. Total
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-gray-300 p-2">
                    Ações
                  </Table.HeadCell>
                </Table.Head>

                <Table.Body className="divide-y">
                  {!isLoadingInvoices ? (
                    <>
                      {invoices?.length > 0 && (
                        <>
                          {invoices.map((invoice, index) => (
                            <Table.Row
                              key={index}
                              className="bg-white dark:border-gray-700 dark:bg-gray-800"
                            >
                              <Table.Cell className="font-medium text-gray-900 dark:text-white p-2">
                                {invoice?.companyName}

                                <span className="block text-2xs -mt-1">
                                  <span>Status: </span>
                                  <span
                                    className={`inline-block font-semibold ${
                                      invoice?.companyStatus === 1
                                        ? "text-green-500"
                                        : invoice?.companyStatus === 0
                                        ? "text-red-500"
                                        : "text-red-500"
                                    }`}
                                  >
                                    {invoice?.companyStatus === 1
                                      ? "Ativo"
                                      : invoice?.companyStatus === 0
                                      ? "Bloqueado"
                                      : "Inadimplente"}
                                  </span>
                                  {` | Plano: `}
                                  {invoice?.typePayment === "POS" ? (
                                    <span className="font-semibold text-blue-700">
                                      Pós-Pago
                                    </span>
                                  ) : (
                                    <span className="font-semibold text-cyan-500">
                                      Pré-Pago
                                    </span>
                                  )}
                                </span>
                              </Table.Cell>

                              <Table.Cell className="font-medium text-gray-900 dark:text-white text-center p-2">
                                {formatToReal(invoice?.rawTotal || 0)}
                              </Table.Cell>

                              <Table.Cell className="font-medium text-gray-900 dark:text-white text-center p-2">
                                {invoice?.vcm
                                  ? formatToReal(invoice?.vcm)
                                  : "-"}
                              </Table.Cell>

                              <Table.Cell className="p-2 text-center">
                                <span className="text-sm font-semibold text-gray-800 border-b border-red-500">
                                  {formatToReal(invoice?.totalBasePrice || 0)}
                                </span>
                              </Table.Cell>

                              {user?.Company?.companyTypeId ===
                                "REPRESENTANTE" && (
                                <Table.Cell className="p-2 text-center">
                                  <span className="text-sm font-semibold text-green-800 border-b border-green-500">
                                    {formatToReal(
                                      +invoice?.adjustedTotalPrice -
                                        +invoice?.totalBasePrice
                                    )}
                                  </span>
                                </Table.Cell>
                              )}

                              <Table.Cell className="p-2 text-center">
                                <span className="text-sm font-semibold text-gray-800 border-b border-green-500">
                                  {formatToReal(invoice?.adjustedTotalPrice)}
                                </span>
                              </Table.Cell>
                              <Table.Cell className="p-2 text-center flex gap-2 justify-center">
                                <button
                                  data-tooltip-id={`tooltip-eye-${invoice.id}`}
                                  data-tooltip-content="Visualizar relatório simplificado"
                                  className="bg-gray-200 rounded-md p-2"
                                  onClick={() => {
                                    setModalPageSintetico(1);
                                    setOpenModalSintetico(true);
                                    setModalCompanyId(invoice.companyId);
                                    setModalCompanyName(
                                      invoice.companyName || ""
                                    );
                                    setModalPage(1);
                                    setInvoiceSelectedForModal(invoice);
                                  }}
                                >
                                  <TbEye fontSize={16} />
                                </button>

                                <button
                                  data-tooltip-id={`tooltip-eye-plus-${invoice.id}`}
                                  data-tooltip-content="Visualizar relatório detalhado"
                                  className="bg-blue-100 rounded-md p-2"
                                  onClick={() => {
                                    setInvoiceSelectedForModal(invoice);
                                    setModalPageDetalhado(1);
                                    setOpenModalDetalhado(true);
                                    setModalCompanyId(invoice.companyId);
                                    setModalCompanyName(
                                      invoice.companyName || ""
                                    );
                                    setModalPage(1);
                                  }}
                                >
                                  <TbEyePlus fontSize={16} />
                                </button>

                                {/* tooltips exclusivos */}
                                <Tooltip id={`tooltip-eye-${invoice.id}`} />
                                <Tooltip
                                  id={`tooltip-eye-plus-${invoice.id}`}
                                />
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <TableLoading cols={6} />
                  )}
                </Table.Body>
              </Table>
            </div>
          ) : (
            <EmptyTable icon="search_off">
              Nenhum registro encontrado com esses filtros
            </EmptyTable>
          )}

          {totalPages > 1 && (
            <div className="flex overflow-x-auto justify-center mt-3">
              <Pagination
                layout="pagination"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                previousLabel=""
                nextLabel=""
                showIcons
              />
            </div>
          )}
        </div>

        <Modal
          show={openModalPaymentConfirm}
          size="xl"
          onClose={() => setOpenModalPaymentConfirm(false)}
          popup
        >
          <ModalHeader />
          <ModalBody>
            <div className="text-center">
              <h3 className="mb-2 text-lg font-normal text-gray-500 dark:text-gray-500">
                Deseja sinalizar este boleto como pago?
              </h3>
              <p className="text-sm text-gray-500">
                Para dar baixa e desbloquear o cliente, clique no botão{" "}
                <b>"Confirmar e Desbloquear"</b>
              </p>
              <div className="flex flex-col md:flex-row justify-center gap-2 mt-5">
                <Button
                  color="success"
                  onClick={() => handleUpdatePayment("CONFIRM")}
                >
                  Apenas confirmar
                </Button>

                <Button
                  color="gray"
                  onClick={() => handleUpdatePayment("CONFIRM_UPDATE")}
                >
                  Confirmar e desbloquear
                </Button>
              </div>
              {errorPaymentConfirm && (
                <Alert className="mt-4" color="failure" withBorderAccent>
                  Ops! Ocorreu algum erro, tente novamente mais tarde!
                </Alert>
              )}
            </div>
          </ModalBody>
        </Modal>

        <Modal
          show={openModalPaymentDelete}
          size="4xl"
          onClose={() => setOpenModalPaymentDelete(false)}
          popup
        >
          <ModalHeader />
          <ModalBody>
            <div className="text-center">
              <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
              <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                Tem certeza que deseja excluir essa cobrança?
              </h3>
              <div className="flex justify-center gap-2">
                <Button color="failure" onClick={() => handleDeletePayment()}>
                  Excluir
                </Button>
                <Button
                  color="gray"
                  onClick={() => setOpenModalPaymentDelete(false)}
                >
                  Cancelar
                </Button>
              </div>
              {errorPaymentDelete && (
                <Alert className="mt-4" color="failure" withBorderAccent>
                  Ops! Ocorreu algum erro, tente novamente mais tarde!
                </Alert>
              )}
            </div>
          </ModalBody>
        </Modal>

        {/* ===== MODAL: RODAR FECHAMENTO (com edição de preços) ===== */}
        <Modal
          show={openModalRunClosing}
          size="4xl"
          onClose={() => setOpenModalRunClosing(false)}
          popup
        >
          <ModalHeader />
          <ModalBody>
            <div className="text-center mb-8">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-white">
                Selecione os clientes para gerar os boletos
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Período:{" "}
                <span className="font-medium">
                  {startDate && endDate
                    ? `${moment(startDate).format("DD/MM/YYYY")} até ${moment(
                        endDate
                      ).format("DD/MM/YYYY")}`
                    : "Nenhum Período selecionado"}
                </span>{" "}
                {startDate &&
                  endDate &&
                  `(${
                    moment(endDate).diff(moment(startDate), "days") + 1
                  } dias)`}
              </p>
            </div>

            {isLoadingInvoices || isLoadingCustomers ? (
              <Spinner />
            ) : (
              (() => {
                const filteredInvoices =
                  invoices?.filter(
                    (invoice) =>
                      invoice.companyId !== 1 &&
                      invoice.typePayment === "POS" &&
                      invoice.adjustedTotalPrice > 0
                  ) || [];

                const allSelected = filteredInvoices.every((inv) =>
                  selectedCustomersForClosing.some(
                    (sel) => sel.companyId === inv.companyId
                  )
                );

                const ensureEditedPriceDefault = (inv) => {
                  setEditedPrices((prev) => {
                    if (prev[inv.companyId] != null) return prev;
                    return {
                      ...prev,
                      [inv.companyId]: numberToBRLTyping(
                        inv.adjustedTotalPrice || 0
                      ),
                    };
                  });
                };

                const toggleSelectAll = () => {
                  if (allSelected) {
                    setSelectedCustomersForClosing([]);
                  } else {
                    setSelectedCustomersForClosing(
                      filteredInvoices.map((inv) => ({
                        companyId: inv.companyId,
                        companyName: inv.companyName,
                        // mantém valor original, mas o custom será lido do editedPrices
                        adjustedTotalPrice: inv.adjustedTotalPrice,
                        asaas_id: inv.asaas_id,
                      }))
                    );
                    // cria defaults para todos
                    setEditedPrices((prev) => {
                      const next = { ...prev };
                      for (const inv of filteredInvoices) {
                        if (next[inv.companyId] == null) {
                          next[inv.companyId] = numberToBRLTyping(
                            inv.adjustedTotalPrice || 0
                          );
                        }
                      }
                      return next;
                    });
                  }
                };

                // total selecionado (considerando preço editado)
                const selectedTotal = (
                  selectedCustomersForClosing || []
                ).reduce((acc, item) => {
                  const view = editedPrices[item.companyId];
                  const value =
                    view != null
                      ? parseBRLInputToNumber(view)
                      : Number(item.adjustedTotalPrice || 0);
                  return acc + value;
                }, 0);

                return (
                  <>
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <div className="text-xs text-gray-600">
                        Selecionados:{" "}
                        <span className="font-semibold">
                          {selectedCustomersForClosing.length}
                        </span>{" "}
                        | Total:{" "}
                        <span className="font-semibold">
                          {formatToReal(selectedTotal)}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="xs"
                          color="gray"
                          onClick={toggleSelectAll}
                        >
                          {allSelected
                            ? `Desmarcar todos (${filteredInvoices.length})`
                            : `Selecionar todos (${filteredInvoices.length})`}
                        </Button>
                        <Button
                          size="xs"
                          color="light"
                          onClick={() => setSelectedCustomersForClosing([])}
                        >
                          Limpar seleção
                        </Button>
                      </div>
                    </div>

                    <div className="bg-gray-100 rounded-md p-3 max-h-80 overflow-y-auto border border-gray-300">
                      {filteredInvoices.length > 0 ? (
                        filteredInvoices.map((invoice) => {
                          const checked = selectedCustomersForClosing.some(
                            (item) => item.companyId === invoice.companyId
                          );
                          const viewValue =
                            editedPrices[invoice.companyId] ??
                            numberToBRLTyping(invoice.adjustedTotalPrice || 0);

                          return (
                            <div
                              key={invoice.companyId}
                              className="grid grid-cols-12 items-center border-b border-gray-200 py-2 px-1 gap-2"
                            >
                              <div className="col-span-6 md:col-span-5 flex items-center gap-2">
                                <Checkbox
                                  id={`customer-${invoice.companyId}`}
                                  checked={checked}
                                  onChange={() => {
                                    setSelectedCustomersForClosing((prev) => {
                                      const alreadySelected = prev.some(
                                        (item) =>
                                          item.companyId === invoice.companyId
                                      );
                                      if (alreadySelected) {
                                        return prev.filter(
                                          (item) =>
                                            item.companyId !== invoice.companyId
                                        );
                                      } else {
                                        // ao selecionar, garante default do preço editado
                                        ensureEditedPriceDefault(invoice);
                                        return [
                                          ...prev,
                                          {
                                            companyId: invoice.companyId,
                                            companyName: invoice.companyName,
                                            adjustedTotalPrice:
                                              invoice.adjustedTotalPrice,
                                            asaas_id: invoice.asaas_id,
                                          },
                                        ];
                                      }
                                    });
                                  }}
                                />
                                <Label
                                  htmlFor={`customer-${invoice.companyId}`}
                                  className="text-sm font-medium text-gray-700 truncate"
                                  title={invoice.companyName}
                                >
                                  {invoice.companyName}
                                </Label>
                              </div>

                              {/* Preço original */}
                              <div className="col-span-3 md:col-span-3 text-right text-xs md:text-sm text-gray-500 mt-1">
                                Orig.:{" "}
                                <span className="font-semibold text-gray-700">
                                  {formatToReal(
                                    Number(invoice?.adjustedTotalPrice || 0)
                                  )}
                                </span>
                              </div>

                              {/* Campo editável (R$) */}
                              <div className="col-span-3 md:col-span-4">
                                <label
                                  htmlFor={`price-${invoice.companyId}`}
                                  className="block text-2xs text-gray-500 mb-0.5"
                                >
                                  Preço customizado (R$)
                                </label>
                                <input
                                  id={`price-${invoice.companyId}`}
                                  type="text"
                                  inputMode="numeric"
                                  className={`w-full block py-1.5 px-2 rounded-md text-sm text-gray-700 bg-white border ${
                                    checked
                                      ? "border-gray-300"
                                      : "border-gray-200 opacity-60"
                                  }`}
                                  value={viewValue}
                                  onFocus={() =>
                                    ensureEditedPriceDefault(invoice)
                                  }
                                  onChange={(e) => {
                                    const next = formatTypingBRL(
                                      e.target.value
                                    );
                                    setEditedPrices((prev) => ({
                                      ...prev,
                                      [invoice.companyId]: next,
                                    }));
                                  }}
                                  disabled={!checked}
                                />
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-sm text-gray-500 italic text-center py-4">
                          Nenhum cliente pós-pago disponível no período
                          selecionado.
                        </div>
                      )}
                    </div>

                    <div className="mt-5 mb-4">
                      <label className="text-sm font-semibold text-gray-700 block mb-1">
                        Data de vencimento do boleto *
                      </label>
                      <input
                        type="date"
                        className="w-full block py-2 px-3 rounded-md text-sm text-gray-700 bg-white border border-gray-300"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        min={moment().format("YYYY-MM-DD")}
                      />
                    </div>
                    <div className="mb-4">
                      <label className="text-sm font-semibold text-gray-700 block mb-1">
                        Descrição do boleto{" "}
                        <span className="font-normal">(opcional)</span>
                      </label>
                      <textarea
                        rows={3}
                        maxLength={500}
                        className="w-full block py-2 px-3 rounded-md text-sm text-gray-700 bg-white border border-gray-300"
                        placeholder="Esta descrição será mostrada ao cliente no boleto — use para observações ou instruções"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                      <div className="text-right text-xs text-gray-500 mt-1">
                        {description.length} / 500 caracteres
                      </div>
                    </div>
                  </>
                );
              })()
            )}

            <div className="flex justify-center mt-5 gap-2">
              <Button
                color="success"
                isProcessing={isSubmittingClosing}
                onClick={async () => {
                  try {
                    setIsSubmittingClosing(true);

                    // Monta payload com preço customizado (quando houver)
                    const payloadCompanies = (
                      selectedCustomersForClosing || []
                    ).map((c) => {
                      const view = editedPrices[c.companyId];
                      const customPrice =
                        view != null
                          ? parseBRLInputToNumber(view)
                          : Number(c.adjustedTotalPrice || 0);

                      return {
                        ...c,
                        customPrice, // <- backend pode ler este campo
                      };
                    });

                    const { data } = await axios.post(`${ADMIN.RUN_CLOSING}`, {
                      companies: payloadCompanies,
                      dueDate,
                      description,
                    });

                    setClosingResult(data);
                    setShowResultModal(true);
                    setOpenModalRunClosing(false);
                    setSelectedCustomersForClosing([]);
                    // (opcional) manter os preços digitados para a próxima execução:
                    // setEditedPrices({});
                    queryClient.invalidateQueries(["invoices"]);
                  } catch (err) {
                    console.error("Erro ao rodar fechamento financeiro", err);
                    alert("Erro ao rodar fechamento financeiro");
                  } finally {
                    setIsSubmittingClosing(false);
                  }
                }}
                disabled={selectedCustomersForClosing.length === 0}
              >
                Rodar fechamento
              </Button>
            </div>
          </ModalBody>
        </Modal>

        <Modal
          show={showResultModal}
          size="lg"
          onClose={() => setShowResultModal(false)}
          popup
        >
          <ModalHeader />
          <ModalBody>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">
                Resultado do fechamento financeiro
              </h3>

              <div className="mb-4">
                <p className="text-sm text-gray-800">
                  <span className="inline-block mr-2">✅</span> Boletos gerados
                  com sucesso para{" "}
                  <strong>{closingResult?.totalProcessed || 0}</strong>{" "}
                  {closingResult?.totalProcessed === 1 ? "empresa" : "empresas"}
                  .
                </p>
              </div>

              {closingResult?.failedPayments?.length > 0 && (
                <div className="text-left mt-4">
                  <p className="text-sm font-semibold text-red-600 mb-2">
                    <span className="inline-block mr-2">❌</span> Falha ao gerar
                    boleto para:
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-700 max-h-40 overflow-y-auto">
                    {closingResult.failedPayments.map((fail, index) => (
                      <li key={index}>
                        {fail.companyName} {fail.error ? `– ${fail.error}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 flex justify-center">
                <Button color="gray" onClick={() => setShowResultModal(false)}>
                  Fechar
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>

        {/* === MODAL: RELATÓRIO SINTÉTICO (EMPRESARIAL) === */}
        <Modal
          show={openModalSintetico}
          size="4xl"
          onClose={() => setOpenModalSintetico(false)}
          popup
        >
          <ModalHeader />
          <ModalBody>
            <div className="space-y-4">
              {/* Cabeçalho */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase text-gray-500">
                    Fechamento financeiro — Sintético
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {invoiceSelectedForModal?.companyName || "Empresa"}
                  </h3>
                </div>
                <div className="text-right">
                  <Button
                    size="xs"
                    color="gray"
                    onClick={async () => {
                      try {
                        const params = new URLSearchParams();
                        if (startDate) params.set("startDate", startDate);
                        if (endDate) params.set("endDate", endDate);

                        const url = `${
                          ADMIN.EXPORT_QUERIES_HISTORY_SIMPLIFIED_REPORT
                        }/${modalCompanyId}?${params.toString()}`;
                        const res = await axios.get(url, {
                          responseType: "blob",
                        });

                        const blob = new Blob([res.data], {
                          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        });

                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = `relatorio_sintetico_${modalCompanyName}_${
                          startDate || "inicio"
                        }_a_${endDate || "fim"}.xlsx`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(a.href);
                      } catch (err) {
                        console.error("Falha ao exportar sintético", err);
                        alert("Erro ao exportar relatório sintético em XLSX");
                      }
                    }}
                  >
                    Exportar XLSX
                  </Button>
                </div>
              </div>

              {/* KPIs topo */}
              {(() => {
                const d = listReportSimplifiedQueries || {};
                const rows = Array.isArray(d.data) ? d.data : [];
                const totalSumPrice =
                  d.totalSumPrice ??
                  rows.reduce((acc, r) => acc + Number(r.sumPrice || 0), 0);
                const totalSumCount =
                  d.totalSumCount ??
                  rows.reduce((acc, r) => acc + Number(r.count || 0), 0);

                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="p-3 rounded-md border bg-white">
                      <div className="text-xs text-gray-500">
                        Consultas (Qtd)
                      </div>
                      <div className="text-lg font-semibold">
                        {formatThousands(totalSumCount || 0)}
                      </div>
                    </div>
                    <div className="p-3 rounded-md border bg-white">
                      <div className="text-xs text-gray-500">
                        Valor Total (R$)
                      </div>
                      <div className="text-lg font-semibold">
                        {currency(totalSumPrice)}
                      </div>
                    </div>
                    <div className="p-3 rounded-md border bg-white">
                      <div className="text-xs text-gray-500">Período</div>
                      <div className="text-sm font-medium">
                        {formatPeriod()}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Conteúdo */}
              {isLoadingReportSimplifiedQueries ||
              isFetchingReportSimplifiedQueries ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : errorListReportSimplifiedQueries ? (
                <Alert color="failure">
                  Não foi possível carregar o relatório sintético.
                </Alert>
              ) : (
                (() => {
                  const d = listReportSimplifiedQueries || {};
                  const rows = Array.isArray(d.data) ? d.data : [];

                  if (!rows.length) {
                    return (
                      <div className="text-gray-500 italic text-center py-6">
                        Sem dados para o período selecionado.
                      </div>
                    );
                  }

                  return (
                    <div className="overflow-x-auto">
                      <Table hoverable style={{ fontSize: 13 }}>
                        <Table.Head>
                          <Table.HeadCell className="bg-gray-100">
                            Consulta
                          </Table.HeadCell>
                          <Table.HeadCell className="bg-gray-100 text-right">
                            Quantidade
                          </Table.HeadCell>
                          <Table.HeadCell className="bg-gray-100 text-right">
                            Total (R$)
                          </Table.HeadCell>
                        </Table.Head>
                        <Table.Body className="divide-y">
                          {rows.map((row, i) => (
                            <Table.Row key={i}>
                              <Table.Cell className="font-medium">
                                {row?.name || row?.queryName || "-"}
                              </Table.Cell>
                              <Table.Cell className="text-right">
                                {formatThousands(row?.count ?? 0)}
                              </Table.Cell>
                              <Table.Cell className="text-right font-semibold">
                                {currency(row?.totalPrice)}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                          {/* totalizador */}
                          <Table.Row>
                            <Table.Cell className="text-right font-semibold">
                              Totais
                            </Table.Cell>
                            <Table.Cell className="text-right font-semibold">
                              {formatThousands(
                                d.totalSumCount ??
                                  rows.reduce(
                                    (a, r) => a + Number(r.count || 0),
                                    0
                                  )
                              ) || 0}
                            </Table.Cell>
                            <Table.Cell className="text-right font-semibold">
                              {currency(
                                d.totalSumPrice ??
                                  rows.reduce(
                                    (a, r) => a + Number(r.sumPrice || 0),
                                    0
                                  )
                              )}
                            </Table.Cell>
                          </Table.Row>
                        </Table.Body>
                      </Table>
                    </div>
                  );
                })()
              )}

              <div className="flex justify-center">
                <Button
                  color="gray"
                  onClick={() => setOpenModalSintetico(false)}
                >
                  Fechar
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>

        {/* === MODAL: RELATÓRIO DETALHADO (EMPRESARIAL) === */}
        <Modal
          show={openModalDetalhado}
          size="4xl"
          onClose={() => setOpenModalDetalhado(false)}
          popup
        >
          <ModalHeader />
          <ModalBody>
            <div className="space-y-4">
              {/* Cabeçalho */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase text-gray-500">
                    Fechamento financeiro — Detalhado
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {invoiceSelectedForModal?.companyName || "Empresa"}
                  </h3>
                </div>
                <div className="text-right">
                  <Button
                    size="xs"
                    color="gray"
                    onClick={async () => {
                      try {
                        const params = new URLSearchParams();
                        if (startDate) params.set("startDate", startDate);
                        if (endDate) params.set("endDate", endDate);

                        params.set("queries", "0");
                        params.set("status", "1");
                        params.set("isModular", "0");

                        const url = `${
                          ADMIN.EXPORT_QUERIES_HISTORY_REPORT
                        }/${modalCompanyId}?${params.toString()}`;
                        const res = await axios.get(url, {
                          responseType: "blob",
                        });

                        const blob = new Blob([res.data], {
                          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        });

                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = `relatorio_detalhado_${modalCompanyName}_${
                          startDate || "inicio"
                        }_a_${endDate || "fim"}.xlsx`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(a.href);
                      } catch (err) {
                        console.error("Falha ao exportar detalhado", err);
                        alert("Erro ao exportar relatório detalhado em XLSX");
                      }
                    }}
                  >
                    Exportar XLSX
                  </Button>
                </div>
              </div>

              {/* KPIs topo (sintese do detalhado) */}
              {(() => {
                const d = listReportQueries || {};
                const rows = Array.isArray(d.data) ? d.data : [];
                const totalCount = d.totalCount ?? rows.length;
                const totalPrice =
                  d.totalPriceSum ??
                  rows.reduce((a, r) => a + Number(r.price || 0), 0);

                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="p-3 rounded-md border bg-white">
                      <div className="text-xs text-gray-500">Registros</div>
                      <div className="text-lg font-semibold">
                        {formatThousands(totalCount || 0)}
                      </div>
                    </div>
                    <div className="p-3 rounded-md border bg-white">
                      <div className="text-xs text-gray-500">
                        Valor Total (R$)
                      </div>
                      <div className="text-lg font-semibold">
                        {currency(totalPrice)}
                      </div>
                    </div>
                    <div className="p-3 rounded-md border bg-white">
                      <div className="text-xs text-gray-500">Período</div>
                      <div className="text-sm font-medium">
                        {formatPeriod()}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Conteúdo */}
              {isLoadingReportQueries || isFetchingReportQueries ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : errorListReportQueries ? (
                <Alert color="failure">
                  Não foi possível carregar o relatório detalhado.
                </Alert>
              ) : (
                (() => {
                  const d = listReportQueries || {};
                  const rows = Array.isArray(d.data) ? d.data : [];

                  if (!rows.length) {
                    return (
                      <div className="text-gray-500 italic text-center py-6">
                        Sem dados detalhados para o período.
                      </div>
                    );
                  }

                  return (
                    <div className="overflow-x-auto">
                      <span className="italic text-xs text-right block mb-1 text-gray-400">
                        Exibindo os últimos 20 registros
                      </span>
                      <Table striped style={{ fontSize: 13 }}>
                        <Table.Head>
                          <Table.HeadCell className="bg-gray-100">
                            Data
                          </Table.HeadCell>
                          <Table.HeadCell className="bg-gray-100">
                            Consulta
                          </Table.HeadCell>
                          <Table.HeadCell className="bg-gray-100">
                            Parâmetro
                          </Table.HeadCell>
                          <Table.HeadCell className="bg-gray-100 text-center">
                            IA
                          </Table.HeadCell>
                          <Table.HeadCell className="bg-gray-100 text-center">
                            Adicional
                          </Table.HeadCell>
                          <Table.HeadCell className="bg-gray-100 text-right">
                            Preço (R$)
                          </Table.HeadCell>
                        </Table.Head>
                        <Table.Body className="divide-y">
                          {rows.map((row, i) => (
                            <Table.Row key={i}>
                              <Table.Cell className="whitespace-nowrap">
                                {row?.createdAt
                                  ? moment(row.createdAt).format(
                                      "DD/MM/YYYY HH:mm"
                                    )
                                  : "-"}
                              </Table.Cell>
                              <Table.Cell className="font-medium">
                                {row?.queryName || row?.name || "-"}
                              </Table.Cell>
                              <Table.Cell className="truncate max-w-[280px]">
                                {row?.parameter || "-"}
                              </Table.Cell>
                              <Table.Cell className="text-center">
                                {row?.hasIA ? "Sim" : "Não"}
                              </Table.Cell>
                              <Table.Cell className="text-center">
                                {resolveAdditionalLabel(row)}
                              </Table.Cell>
                              <Table.Cell className="text-right font-semibold">
                                {currency(row?.price)}
                              </Table.Cell>
                            </Table.Row>
                          ))}

                          {/* totalizador */}
                          <Table.Row>
                            <Table.Cell />
                            <Table.Cell />
                            <Table.Cell className="text-right font-semibold">
                              Total
                            </Table.Cell>
                            <Table.Cell />
                            <Table.Cell />
                            <Table.Cell className="text-right font-semibold">
                              {currency(
                                d.totalPriceSum ??
                                  rows.reduce(
                                    (a, r) => a + Number(r.price || 0),
                                    0
                                  )
                              )}
                            </Table.Cell>
                          </Table.Row>
                        </Table.Body>
                      </Table>
                    </div>
                  );
                })()
              )}

              {/* Paginação opcional do modal (se seu endpoint retornar totalPages) */}
              {/* {listReportQueries?.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={modalPage}
                    totalPages={listReportQueries.totalPages}
                    onPageChange={setModalPage}
                    showIcons
                  />
                </div>
              )} */}

              <div className="flex justify-center">
                <Button
                  color="gray"
                  onClick={() => setOpenModalDetalhado(false)}
                >
                  Fechar
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
};

export default AdminCloseTable;
